import type { ReactNode } from "react";
import { faceDataPolicyHref } from "@/content/faceDataPolicy";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { openConsentSettings } from "@/lib/consent";
import { PREVIEW_FIRST_PATH } from "@/content/previewFirst";
import { WEB_UPLOADER_PATH } from "@/content/webUploader";
import { ON_DEMAND_FTP_PATH } from "@/content/onDemandFtp";
import { LIVE_UPLOADING_PATH } from "@/content/liveUploading";
import { isLiveUploadingEnabled } from "@/lib/liveUploadingFlag";
import { isNewPricing } from "@/lib/plans/entitlements";
import { PRICING_PATHS, changesAnnounced } from "@/content/pricing/plans";
import { GUIDES_PATH, isGuidesEnabled } from "@/lib/guidesFlag";
import { HELP_URL } from "@/lib/seo/site";
import { HELP_PATH, isHelpEnabled } from "@/lib/helpFlag";
import { useHostname } from "@/lib/ssr";
import { EAGLE_DARK } from "./images";
import { APP_LEGAL_BASE, APP_PATH, DOWNLOAD_PATH, FIND_PATH, LOGIN_PATH, PERSONAL_APP_STORE_URL, REGISTER_PATH, STUDIO_PATH, STUDIO_PRICING } from "./paths";
import { PRODUCT_NAME, type Product } from "./products";
import { FOCUS } from "./ui";

// The one footer of the new site: every link of the site footer (src/components/Footer.tsx) and the old home
// footer, in a column per product (Photohawk Studio first, then the Photohawk app: each named), plus the company
// links and the legal links of both products (the app's stay under /personal/*). The Studio column has the Studio
// log-in, sign-up and the Studio apps (/download); the app's column has its page, the App Store and "Android: coming
// soon", and says you sign in inside the app, so nobody downloads the wrong app. Keep it in step with the site footer. Light, like the rest of the site: the dashboard's muted band, its
// border and its muted text (src/pages/home/ui.ts).
export const TERMS_URL = "https://app.termly.io/policy-viewer/policy.html?policyUUID=19d74052-fc1f-4cf3-8445-01a26252455b";

const SOCIAL = [
  {
    label: "Facebook",
    href: "https://facebook.com/thephotohawk",
    icon: <path d="M15 3h-2.5A3.5 3.5 0 0 0 9 6.5V9H7v3.5h2V21h3.5v-8.5H15l.5-3.5h-3V7a1 1 0 0 1 1-1H15z" />,
  },
  {
    label: "Instagram",
    href: "https://instagram.com/photohawk.io",
    icon: (
      <>
        <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.3" cy="6.7" r="0.6" />
      </>
    ),
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/photohawk",
    icon: (
      <>
        <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" />
        <path d="M8 10.5V16M8 7.8v.1M11.5 16v-3.2a2.3 2.3 0 0 1 4.6 0V16M11.5 10.5V16" />
      </>
    ),
  },
];

const LINK = `flex min-h-9 items-center gap-2 rounded-md text-muted-foreground no-underline transition-colors hover:text-foreground ${FOCUS}`;
const FINE = `flex min-h-9 items-center rounded-md text-muted-foreground transition-colors hover:text-foreground ${FOCUS}`;

// The column labels are small uppercase captions; Sora's capitals are too wide for the 180 px columns, so where the
// Sora headings are on (src/lib/siteFont.ts) they're set in Inter like the site's other eyebrow labels.
const Column = ({ title, product, children }: { title: string; product?: Product; children: ReactNode }) => (
  <div className="flex flex-col gap-1" data-footer-product={product}>
    <h2 className="mb-1.5 text-xs font-bold uppercase tracking-[0.1em] text-foreground sora:font-sans sora:tracking-[0.08em]">
      {title}
    </h2>
    {children}
  </div>
);

/** A sub-heading inside a column (the legal links of each product). */
const SubTitle = ({ children }: { children: ReactNode }) => (
  <h3 className="mb-0.5 mt-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground first:mt-0 sora:font-sans sora:tracking-[0.08em]">{children}</h3>
);

const NewBadge = ({ label }: { label: string }) => (
  <span className="rounded-full bg-[#EA8A23] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#121721]">{label}</span>
);

const HomeFooter = () => {
  const { t } = useTranslation();
  // The guides: only where they're on (staging until the owner approves them).
  const host = useHostname();
  const guides = isGuidesEnabled(host);
  const now = Date.now();
  return (
    <footer className="border-t border-border bg-muted text-muted-foreground" data-testid="home-footer">
      <div className="mx-auto flex max-w-[1320px] flex-wrap gap-x-14 gap-y-10 px-[clamp(16px,4vw,56px)] pb-8 pt-14 text-sm">
        <div className="flex min-w-0 max-w-[320px] flex-[1_1_240px] flex-col gap-4">
          <div className="flex items-center gap-2.5">
            <img src={EAGLE_DARK} alt="" width={32} height={33} className="h-[33px] w-8 object-contain" />
            <span className="font-display text-[19px] font-bold text-foreground">Photohawk</span>
          </div>
          <p className="m-0 leading-relaxed text-muted-foreground">{t("home.footer.tagline")}</p>
          <div className="flex gap-2.5" role="group" aria-label={t("home.footer.social")}>
            {SOCIAL.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className={`flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ${FOCUS}`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {s.icon}
                </svg>
              </a>
            ))}
          </div>
        </div>

        <nav aria-label={t("home.footer.nav")} className="grid min-w-0 flex-[3_1_640px] grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-x-6 gap-y-8">
          <Column title={PRODUCT_NAME.studio} product="studio">
            <Link to={STUDIO_PATH} className={LINK}>
              {t("home.footer.overview")}
            </Link>
            <Link to={`${STUDIO_PATH}#features`} className={LINK}>
              {t("footer.features")}
            </Link>
            <Link to={DOWNLOAD_PATH} className={LINK} data-testid="footer-studio-apps">
              {t("home.nav.studioApps")}
            </Link>
            <Link to="/for-photographers" className={LINK}>
              {t("footer.forPhotographers")}
            </Link>
            <Link to="/for-organisers" className={LINK}>
              {t("footer.forOrganisers")}
            </Link>
            <Link to="/for-participants" className={LINK}>
              {t("footer.forParticipants")}
            </Link>
            <Link to={FIND_PATH} className={LINK}>
              {t("nav.findPhotos")}
            </Link>
            <Link to={WEB_UPLOADER_PATH} className={LINK}>
              {t("footer.webUploader")} <NewBadge label={t("footer.new")} />
            </Link>
            <Link to={PREVIEW_FIRST_PATH} className={LINK}>
              {t("footer.previewFirst")} <NewBadge label={t("footer.new")} />
            </Link>
            <Link to={ON_DEMAND_FTP_PATH} className={LINK}>
              {t("footer.onDemandFtp")}
            </Link>
            {isLiveUploadingEnabled(host) && (
              <Link to={LIVE_UPLOADING_PATH} className={LINK}>
                {t("footer.liveUploading")} <NewBadge label={t("footer.new")} />
              </Link>
            )}
            <Link to="/preview-protection" className={LINK}>
              {t("footer.previewProtection")}
            </Link>
            <Link to={STUDIO_PRICING} className={LINK}>
              {t("nav.pricing")}
            </Link>
            {changesAnnounced(now) && (
              <Link to={PRICING_PATHS.changes} className={LINK}>
                {t("footer.pricingChanges")}
              </Link>
            )}
            <Link to={LOGIN_PATH} className={LINK}>
              {t("home.nav.studioLogIn")}
            </Link>
            <Link to={REGISTER_PATH} className={LINK}>
              {t("home.nav.startStudio")}
            </Link>
          </Column>
          <Column title={t("home.nav.photohawkApp")} product="personal">
            <Link to={APP_PATH} className={LINK} data-testid="footer-app">
              {t("home.footer.overview")}
            </Link>
            <a href={PERSONAL_APP_STORE_URL} className={LINK} data-testid="footer-app-store">
              {t("home.band.appStore")}
            </a>
            <p className="m-0 text-[13px] leading-snug text-muted-foreground">{t("home.band.android")}</p>
            <p className="m-0 mt-1 max-w-[16em] text-[13px] leading-snug text-muted-foreground" data-testid="footer-personal-signin">
              {t("home.doors.personal.signIn")}
            </p>
          </Column>
          <Column title={t("footer.company")}>
            <Link to="/about" className={LINK}>
              {t("footer.aboutUs")}
            </Link>
            {guides && (
              <Link to={GUIDES_PATH} className={LINK}>
                {t("footer.guides")}
              </Link>
            )}
            <Link to="/blog" className={LINK}>
              {t("footer.blog")}
            </Link>
            {/* /help where the new help centre is on (src/lib/helpFlag.ts), else the Intercom Help Center. */}
            {isHelpEnabled(host) ? (
              <Link to={HELP_PATH} className={LINK}>
                {t("footer.help")}
              </Link>
            ) : (
              <a href={HELP_URL} className={LINK}>
                {t("footer.help")}
              </a>
            )}
            <a href="mailto:support@photohawk.com" className={LINK}>
              {t("footer.contact")}
            </a>
          </Column>
          <Column title={t("footer.legal")}>
            <SubTitle>{PRODUCT_NAME.studio}</SubTitle>
            <Link to="/privacy" className={LINK}>
              {t("footer.privacyPolicy")}
            </Link>
            {/* As in the site footer: listed from 1 November 2026, when the policy takes effect. */}
            {isNewPricing(now) && (
              <Link to="/biometric-data-retention" className={LINK}>
                {t("footer.biometricRetention")}
              </Link>
            )}
            <Link to={faceDataPolicyHref("studio")} className={LINK}>
              {t("footer.faceDataPolicy")}
            </Link>
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={LINK}>
              {t("footer.termsOfService")}
            </a>
            <SubTitle>{PRODUCT_NAME.personal}</SubTitle>
            <Link to={`${APP_LEGAL_BASE}/privacy`} className={LINK}>
              {t("home.footer.appPrivacy")}
            </Link>
            <Link to={faceDataPolicyHref("family")} className={LINK}>
              {t("home.footer.appFaceData")}
            </Link>
            <Link to={`${APP_LEGAL_BASE}/terms`} className={LINK}>
              {t("home.footer.appTerms")}
            </Link>
            <Link to={`${APP_LEGAL_BASE}/delete-account`} className={LINK}>
              {t("home.footer.appDeleteAccount")}
            </Link>
          </Column>
        </nav>
      </div>
      <div className="mx-auto flex max-w-[1320px] flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-border px-[clamp(16px,4vw,56px)] pb-7 pt-[18px] text-[13px] text-muted-foreground">
        <span>
          © {new Date().getFullYear()} Photohawk. {t("footer.allRightsReserved")}
        </span>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <Link to="/privacy" className={FINE}>
            {t("footer.privacy")}
          </Link>
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={FINE}>
            {t("footer.terms")}
          </a>
          <Link to="/privacy" className={FINE}>
            {t("footer.cookies")}
          </Link>
          <button type="button" onClick={openConsentSettings} className={`min-h-9 rounded-md border-0 bg-transparent p-0 text-[13px] text-muted-foreground transition-colors hover:text-foreground ${FOCUS}`}>
            {t("footer.cookieSettings")}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default HomeFooter;
